import React, { useState, useEffect, useRef } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box, Paper, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, CircularProgress, IconButton, Collapse, Grid, Card, CardContent,
  Tooltip, Autocomplete, Tabs, Tab, InputAdornment, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search, Refresh, Timeline, PersonAdd, AttachMoney, SportsEsports, EmojiEvents,
  Casino, KeyboardArrowDown, KeyboardArrowUp, FiberManualRecord, Download, Upload,
  AccountBalanceWallet, TrendingUp, SwapHoriz, Info, CheckCircle, Cancel,
  HourglassEmpty, Layers, FilterList
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import UserDetailsModal from '../components/UserDetailsModal';
import { formatCurrency } from '../utils/formatters';
import io from 'socket.io-client';
import axios from 'axios';

// API для статистики слотов
const SLOTS_API = 'https://slots.moonraid.online/api/admin/slots';

// 🔒 Функция для получения заголовков авторизации
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CATEGORIES = [
  { value: 'user', label: 'Пользователи', icon: <PersonAdd /> },
  { value: 'finance', label: 'Финансы', icon: <AttachMoney /> },
  { value: 'minigame', label: 'Мини-игры', icon: <SportsEsports /> },
  { value: 'status', label: 'Статусы', icon: <EmojiEvents /> },
  { value: 'slots', label: 'Слоты', icon: <Casino /> }
];

const CATEGORY_COLORS = {
  user: '#2196f3',
  finance: '#4caf50',
  minigame: '#ff9800',
  status: '#9c27b0',
  slots: '#ffc107'
};

const EVENT_TYPES = {
  user: [
    { value: 'user_registered', label: 'Регистрация' },
    { value: 'user_invited', label: 'Приглашение' }
  ],
  finance: [
    { value: 'claim', label: 'Сбор прибыли' },
    { value: 'deposit', label: 'Депозит' },
    { value: 'withdrawal_requested', label: 'Запрос вывода' },
    { value: 'withdrawal_completed', label: 'Вывод завершен' },
    { value: 'staking_started', label: 'Стейкинг начат' },
    { value: 'staking_completed', label: 'Стейкинг завершен' }
  ],
  minigame: [
    { value: 'minigame_played', label: 'Мини-игра сыграна' }
  ],
  status: [
    { value: 'rank_achieved', label: 'Достигнут ранг' },
    { value: 'vip_achieved', label: 'Достигнут VIP' },
    { value: 'leader_achieved', label: 'Достигнут статус лидера' }
  ],
  slots: [
    { value: 'gamble_win', label: 'Карта: Выигрыш' },
    { value: 'gamble_lose', label: 'Карта: Проигрыш' }
  ]
};

const RealTimeActivity = () => {
  // ========== Вкладки ==========
  const [activeTab, setActiveTab] = usePersistedTab('activity', 0);

  // ========== Активность (вкладка 0) ==========
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [period, setPeriod] = useState('day');
  const [selectedCategories, setSelectedCategories] = useState(['user', 'finance', 'minigame', 'status', 'slots']);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [connected, setConnected] = useState(false);
  const [slotsRtp, setSlotsRtp] = useState(null);
  const [rtpLoading, setRtpLoading] = useState(true);
  const socketRef = useRef(null);

  // ========== Транзакции (вкладка 1) ==========
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [txSearchTerm, setTxSearchTerm] = useState('');
  const [transactionType, setTransactionType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [detailsDialog, setDetailsDialog] = useState({ open: false, transaction: null });
  const [txStats, setTxStats] = useState({
    totalTransactions: 0,
    totalVolume: 0,
    deposits: 0,
    withdrawals: 0,
    pendingAmount: 0,
    successRate: 0
  });

  // Состояние для модального окна пользователя
  const [userDetailsModal, setUserDetailsModal] = useState({ open: false, userId: null });

  // ========== Загрузка RTP слотов ==========
  const loadSlotsRtp = async () => {
    try {
      const res = await axios.get(`${SLOTS_API}/stats`, { headers: getAuthHeaders() });
      if (res.data && res.data.general) {
        const { total_bets, total_wins, total_spins, jackpots } = res.data.general;
        const rtp = total_bets > 0 ? (total_wins / total_bets) * 100 : 0;
        const profit = total_bets - total_wins;
        setSlotsRtp({
          rtp: rtp.toFixed(2),
          totalBets: total_bets,
          totalWins: total_wins,
          totalSpins: total_spins,
          jackpots: jackpots || 0,
          profit: profit
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки RTP слотов:', error);
    } finally {
      setRtpLoading(false);
    }
  };

  useEffect(() => {
    loadSlotsRtp();
    const rtpInterval = setInterval(loadSlotsRtp, 3000);
    return () => clearInterval(rtpInterval);
  }, []);

  // ========== Загрузка активности ==========
  const loadActivities = async () => {
    try {
      setActivityLoading(true);
      const params = { page, limit: 50 };

      if (selectedEventTypes.length > 0) {
        params.eventTypes = selectedEventTypes;
      } else if (selectedCategories.length > 0 && selectedCategories.length < 6) {
        params.categories = selectedCategories;
      }

      if (searchQuery) params.username = searchQuery;

      const now = new Date();
      let startDate;
      switch (period) {
        case 'hour': startDate = new Date(now.getTime() - 60 * 60 * 1000); break;
        case 'day': startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000); break;
        case 'week': startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
        case 'month': startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); break;
        default: startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      }

      params.startDate = startDate.toISOString();
      params.endDate = now.toISOString();

      const response = await adminAPI.get('/activity/realtime', { params });

      if (response.data.success) {
        setActivities(response.data.data);
        setTotal(response.data.pagination.total);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Ошибка загрузки активности:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 0) {
      loadActivities();
    }
  }, [page, selectedCategories, selectedEventTypes, period, activeTab]);

  // ========== WebSocket ==========
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    socketRef.current = io(process.env.REACT_APP_BACKEND_URL || 'https://moonraid.online', {
      auth: { token }
    });

    socketRef.current.on('connect', () => {
      setConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      setConnected(false);
    });

    socketRef.current.on('activity:new', (activity) => {
      if (page === 1 && activeTab === 0) {
        setActivities(prev => [activity, ...prev].slice(0, 50));
        setTotal(prev => prev + 1);
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [page, activeTab]);

  // ========== Загрузка транзакций ==========
  const loadTransactions = async () => {
    try {
      setTxLoading(true);
      const response = await adminAPI.getTransactions({ page: 1, limit: 500 });

      if (response.data.success && response.data.data) {
        const transactionsData = Array.isArray(response.data.data)
          ? response.data.data
          : (response.data.data.transactions || []);
        setTransactions(transactionsData);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки транзакций:', error);
      setTransactions([]);
    } finally {
      setTxLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      loadTransactions();
    }
  }, [activeTab]);

  useEffect(() => {
    if (transactions.length > 0) {
      const totalVolume = transactions.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
      const deposits = transactions
        .filter(t => t.type === 'deposit')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      const withdrawals = transactions
        .filter(t => t.type === 'withdrawal')
        .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
      const pending = transactions
        .filter(t => t.status === 'pending')
        .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
      const completed = transactions.filter(t => t.status === 'completed').length;
      const successRate = transactions.length > 0 ? (completed / transactions.length * 100) : 0;

      setTxStats({
        totalTransactions: transactions.length,
        totalVolume,
        deposits,
        withdrawals,
        pendingAmount: pending,
        successRate
      });
    }
  }, [transactions]);

  // ========== Хелперы ==========
  const handleSearch = () => {
    setPage(1);
    loadActivities();
  };

  const getEventIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : <Timeline />;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return `${diff} сек назад`;
    if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
    return date.toLocaleString('ru-RU');
  };

  const getTransactionIcon = (type) => {
    const icons = {
      'deposit': <Download sx={{ color: '#10b981' }} />,
      'withdrawal': <Upload sx={{ color: '#ef4444' }} />,
      'claim': <AccountBalanceWallet sx={{ color: '#6366f1' }} />,
      'staking_reward': <TrendingUp sx={{ color: '#f59e0b' }} />,
      'staking_deposit': <Layers sx={{ color: '#8b5cf6' }} />,
      'minigame': <SportsEsports sx={{ color: '#ec4899' }} />,
      'referral': <SwapHoriz sx={{ color: '#14b8a6' }} />,
      'task_reward': <CheckCircle sx={{ color: '#10b981' }} />
    };
    return icons[type] || <AttachMoney />;
  };

  const getTransactionTypeName = (type) => {
    const names = {
      'deposit': 'Пополнение',
      'withdrawal': 'Вывод',
      'claim': 'CLAIM',
      'staking_reward': 'Стейкинг доход',
      'staking_deposit': 'Стейкинг депозит',
      'minigame': 'Мини-игра',
      'referral': 'Реферал',
      'task_reward': 'Задание'
    };
    return names[type] || type;
  };

  // ========== Колонки таблицы транзакций ==========
  const txColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'type',
      headerName: 'Тип',
      width: 50,
      renderCell: (params) => (
        <Tooltip title={getTransactionTypeName(params.value)}>
          {getTransactionIcon(params.value)}
        </Tooltip>
      )
    },
    {
      field: 'username',
      headerName: 'Пользователь',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: '#6366f1', fontSize: 12 }}>
            {(params.value || '?')[0].toUpperCase()}
          </Avatar>
          <Typography sx={{ fontSize: 14 }}>
            {params.value || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'description',
      headerName: 'Описание',
      width: 200,
      renderCell: (params) => (
        <Typography sx={{ fontSize: 13 }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'amount',
      headerName: 'Сумма',
      width: 120,
      renderCell: (params) => (
        <Typography sx={{
          fontWeight: 'bold',
          color: params.value >= 0 ? '#10b981' : '#ef4444',
          fontSize: 14
        }}>
          {params.value >= 0 ? '+' : ''}{formatCurrency(params.value)}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Статус',
      width: 110,
      renderCell: (params) => {
        const config = {
          'completed': { label: 'Выполнено', color: 'success', icon: <CheckCircle /> },
          'pending': { label: 'Ожидание', color: 'warning', icon: <HourglassEmpty /> },
          'processing': { label: 'Обработка', color: 'info', icon: <HourglassEmpty /> },
          'failed': { label: 'Ошибка', color: 'error', icon: <Cancel /> }
        };
        const status = config[params.value] || { label: params.value, color: 'default' };
        return (
          <Chip
            label={status.label}
            size="small"
            color={status.color}
            icon={status.icon}
            sx={{ fontSize: 12 }}
          />
        );
      }
    },
    {
      field: 'created_at',
      headerName: 'Время',
      width: 140,
      renderCell: (params) => formatTime(params.value)
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => setDetailsDialog({ open: true, transaction: params.row })}
        >
          <Info />
        </IconButton>
      )
    }
  ];

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.username?.toLowerCase().includes(txSearchTerm.toLowerCase()) ||
      transaction.id?.toString().includes(txSearchTerm) ||
      transaction.description?.toLowerCase().includes(txSearchTerm.toLowerCase());
    const matchesType = transactionType === 'all' || transaction.type === transactionType;
    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  // ========== Рендер ==========
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timeline />
          Активность Live
          {connected && (
            <FiberManualRecord sx={{ fontSize: 12, color: '#4caf50', animation: 'pulse 2s infinite' }} />
          )}
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={activeTab === 0 ? loadActivities : loadTransactions}
        >
          Обновить
        </Button>
      </Box>

      {/* Вкладки */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newVal) => setActiveTab(newVal)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Активность" />
          <Tab label="Транзакции" />
        </Tabs>
      </Paper>

      {/* ========== ВКЛАДКА 0: АКТИВНОСТЬ ========== */}
      {activeTab === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </Box>

          <Collapse in={showFilters}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Поиск по username или ID"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Период</InputLabel>
                    <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
                      <MenuItem value="hour">Последний час</MenuItem>
                      <MenuItem value="day">День</MenuItem>
                      <MenuItem value="week">Неделя</MenuItem>
                      <MenuItem value="month">Месяц</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button fullWidth variant="contained" startIcon={<Search />} onClick={handleSearch}>
                    Применить
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={CATEGORIES}
                    getOptionLabel={(option) => option.label}
                    value={CATEGORIES.filter(c => selectedCategories.includes(c.value))}
                    onChange={(e, newValue) => {
                      setSelectedCategories(newValue.map(v => v.value));
                      setPage(1);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Категории" placeholder="Выберите..." />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          size="small"
                          label={option.label}
                          {...getTagProps({ index })}
                          sx={{
                            bgcolor: `${CATEGORY_COLORS[option.value]}22`,
                            color: CATEGORY_COLORS[option.value]
                          }}
                        />
                      ))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={selectedCategories.flatMap(cat =>
                      (EVENT_TYPES[cat] || []).map(et => ({ ...et, category: cat }))
                    )}
                    getOptionLabel={(option) => option.label}
                    groupBy={(option) => CATEGORIES.find(c => c.value === option.category)?.label || ''}
                    value={selectedCategories.flatMap(cat =>
                      (EVENT_TYPES[cat] || []).filter(et => selectedEventTypes.includes(et.value))
                    )}
                    onChange={(e, newValue) => {
                      setSelectedEventTypes(newValue.map(v => v.value));
                      setPage(1);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Типы событий" placeholder="Все события..." />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip size="small" label={option.label} {...getTagProps({ index })} />
                      ))
                    }
                    disabled={selectedCategories.length === 0}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Collapse>

          {/* Карточки статистики */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6">
                    📊 Всего событий: {total.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{
                height: '100%',
                bgcolor: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.3)'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Casino sx={{ fontSize: 16 }} /> RTP Слотов
                        <Tooltip title="Автообновление каждые 3 сек">
                          <FiberManualRecord sx={{ fontSize: 8, color: '#4caf50', animation: 'pulse 2s infinite' }} />
                        </Tooltip>
                      </Typography>
                      {rtpLoading ? (
                        <CircularProgress size={24} sx={{ mt: 1 }} />
                      ) : slotsRtp ? (
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 'bold',
                            color: slotsRtp.rtp > 98 ? '#f44336' : slotsRtp.rtp > 94 ? '#ff9800' : '#4caf50'
                          }}
                        >
                          {slotsRtp.rtp}%
                        </Typography>
                      ) : (
                        <Typography variant="h4" color="text.secondary">—</Typography>
                      )}
                    </Box>
                    {slotsRtp && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Спинов: {slotsRtp.totalSpins?.toLocaleString() || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Ставки: {Number(slotsRtp.totalBets || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{
                          color: slotsRtp.profit > 0 ? '#4caf50' : '#f44336',
                          fontWeight: 'bold'
                        }}>
                          Профит: {slotsRtp.profit > 0 ? '+' : ''}{Number(slotsRtp.profit || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {activityLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Время</TableCell>
                      <TableCell>Категория</TableCell>
                      <TableCell>Пользователь</TableCell>
                      <TableCell>Событие</TableCell>
                      <TableCell>Сумма</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.map((activity, index) => (
                      <TableRow
                        key={activity.id}
                        sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                          animation: index === 0 && page === 1 ? 'fadeIn 0.5s' : 'none',
                          bgcolor: activity.event_type === 'gamble_win'
                            ? 'rgba(76, 175, 80, 0.15)'
                            : activity.event_type === 'gamble_lose'
                              ? 'rgba(244, 67, 54, 0.15)'
                              : 'inherit'
                        }}
                      >
                        <TableCell>{formatTime(activity.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getEventIcon(activity.event_category)}
                            label={activity.event_category}
                            size="small"
                            sx={{
                              bgcolor: `${CATEGORY_COLORS[activity.event_category]}22`,
                              color: CATEGORY_COLORS[activity.event_category],
                              borderColor: CATEGORY_COLORS[activity.event_category]
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              onClick={() => {
                                if (activity.user_id) {
                                  setUserDetailsModal({ open: true, userId: activity.user_id });
                                }
                              }}
                              sx={{
                                cursor: activity.user_id ? 'pointer' : 'default',
                                color: activity.user_id ? '#6366f1' : 'inherit',
                                '&:hover': activity.user_id ? {
                                  textDecoration: 'underline',
                                  color: '#818cf8'
                                } : {}
                              }}
                            >
                              {activity.telegram_username
                                ? `@${activity.telegram_username}`
                                : activity.first_name
                                  ? `${activity.first_name} (ID: ${activity.telegram_id})`
                                  : activity.username || 'System'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {activity.event_type === 'gamble_win' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label="🃏 КАРТА: ВЫИГРАЛ"
                                size="small"
                                sx={{ bgcolor: '#4caf50', color: '#fff', fontWeight: 'bold' }}
                              />
                            </Box>
                          ) : activity.event_type === 'gamble_lose' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label="🃏 КАРТА: ПРОИГРАЛ"
                                size="small"
                                sx={{ bgcolor: '#f44336', color: '#fff', fontWeight: 'bold' }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="body2">{activity.event_description}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {activity.amount && (
                            <Typography variant="body2" fontWeight="bold">
                              {parseFloat(activity.amount).toLocaleString()} SPERM
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </>
      )}

      {/* ========== ВКЛАДКА 1: ТРАНЗАКЦИИ ========== */}
      {activeTab === 1 && (
        <>
          {/* Статистика транзакций */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography color="white" variant="body2" sx={{ fontSize: 11 }}>
                    Всего транзакций
                  </Typography>
                  <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 0.5 }}>
                    {txStats.totalTransactions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography color="white" variant="body2" sx={{ fontSize: 11 }}>
                    Общий объем
                  </Typography>
                  <Typography color="white" variant="h6" sx={{ fontWeight: 'bold', my: 0.5, fontSize: 16 }}>
                    {(txStats.totalVolume / 1000).toFixed(1)}K
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography color="white" variant="body2" sx={{ fontSize: 11 }}>
                    Пополнения
                  </Typography>
                  <Typography color="white" variant="h6" sx={{ fontWeight: 'bold', my: 0.5, fontSize: 16 }}>
                    +{(txStats.deposits / 1000).toFixed(1)}K
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography color="white" variant="body2" sx={{ fontSize: 11 }}>
                    Выводы
                  </Typography>
                  <Typography color="white" variant="h6" sx={{ fontWeight: 'bold', my: 0.5, fontSize: 16 }}>
                    -{(txStats.withdrawals / 1000).toFixed(1)}K
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography color="white" variant="body2" sx={{ fontSize: 11 }}>
                    В ожидании
                  </Typography>
                  <Typography color="white" variant="h6" sx={{ fontWeight: 'bold', my: 0.5, fontSize: 16 }}>
                    {(txStats.pendingAmount / 1000).toFixed(1)}K
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Card sx={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography color="white" variant="body2" sx={{ fontSize: 11 }}>
                    Успешность
                  </Typography>
                  <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 0.5 }}>
                    {txStats.successRate.toFixed(0)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Фильтры транзакций */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Поиск по пользователю, ID или описанию..."
                variant="outlined"
                size="small"
                value={txSearchTerm}
                onChange={(e) => setTxSearchTerm(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Тип</InputLabel>
                <Select
                  value={transactionType}
                  label="Тип"
                  onChange={(e) => setTransactionType(e.target.value)}
                >
                  <MenuItem value="all">Все</MenuItem>
                  <MenuItem value="deposit">Пополнения</MenuItem>
                  <MenuItem value="withdrawal">Выводы</MenuItem>
                  <MenuItem value="claim">CLAIM</MenuItem>
                  <MenuItem value="staking_reward">Стейкинг</MenuItem>
                  <MenuItem value="minigame">Мини-игры</MenuItem>
                  <MenuItem value="referral">Рефералы</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={statusFilter}
                  label="Статус"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">Все</MenuItem>
                  <MenuItem value="completed">Выполнено</MenuItem>
                  <MenuItem value="pending">Ожидание</MenuItem>
                  <MenuItem value="processing">Обработка</MenuItem>
                  <MenuItem value="failed">Ошибка</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Таблица транзакций */}
          <Paper sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={filteredTransactions}
              columns={txColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              loading={txLoading}
              getRowId={(row) => row.id}
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#1e293b',
                  borderBottom: '2px solid #6366f1'
                }
              }}
            />
          </Paper>

          {/* Диалог деталей транзакции */}
          <Dialog
            open={detailsDialog.open}
            onClose={() => setDetailsDialog({ open: false, transaction: null })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {detailsDialog.transaction && getTransactionIcon(detailsDialog.transaction.type)}
                <Typography sx={{ ml: 1 }}>
                  Транзакция #{detailsDialog.transaction?.id}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              {detailsDialog.transaction && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Пользователь</Typography>
                    <Typography variant="body1">{detailsDialog.transaction.username}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Тип операции</Typography>
                    <Typography variant="body1">{getTransactionTypeName(detailsDialog.transaction.type)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Сумма</Typography>
                    <Typography variant="h6" sx={{
                      color: detailsDialog.transaction.amount >= 0 ? '#10b981' : '#ef4444',
                      fontWeight: 'bold'
                    }}>
                      {detailsDialog.transaction.amount >= 0 ? '+' : ''}
                      {formatCurrency(detailsDialog.transaction.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Статус</Typography>
                    <Chip
                      label={
                        detailsDialog.transaction.status === 'completed' ? 'Выполнено' :
                          detailsDialog.transaction.status === 'pending' ? 'Ожидание' :
                            detailsDialog.transaction.status === 'processing' ? 'Обработка' : 'Ошибка'
                      }
                      size="small"
                      color={
                        detailsDialog.transaction.status === 'completed' ? 'success' :
                          detailsDialog.transaction.status === 'pending' ? 'warning' :
                            detailsDialog.transaction.status === 'processing' ? 'info' : 'error'
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Описание</Typography>
                    <Typography variant="body1">{detailsDialog.transaction.description}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Время создания</Typography>
                    <Typography variant="body1">
                      {new Date(detailsDialog.transaction.created_at).toLocaleString('ru-RU')}
                    </Typography>
                  </Grid>
                  {detailsDialog.transaction.tx_hash && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Hash транзакции</Typography>
                      <Paper sx={{ p: 1, mt: 1, bgcolor: 'background.default' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                          {detailsDialog.transaction.tx_hash}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              {detailsDialog.transaction?.status === 'pending' && (
                <>
                  <Button color="success" variant="contained">Подтвердить</Button>
                  <Button color="error" variant="outlined">Отклонить</Button>
                </>
              )}
              <Button onClick={() => setDetailsDialog({ open: false, transaction: null })}>
                Закрыть
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Модальное окно с деталями пользователя */}
      <UserDetailsModal
        open={userDetailsModal.open}
        onClose={() => setUserDetailsModal({ open: false, userId: null })}
        userId={userDetailsModal.userId}
      />
    </Box>
  );
};

export default RealTimeActivity;
